/**
 * Centralized feature-flag reads.
 *
 * All flags here are environment-driven (VITE_* env vars). Defaults are
 * conservative: flags ship "off" so legacy code paths stay intact unless
 * the deployment explicitly enables the new behavior.
 *
 * Add new flags by adding an exported function — keep one flag per export
 * so call sites grep cleanly.
 */

/**
 * V2 admin module — Properties / Rooms / Tenants redesign per the
 * 2026-04-27 design handoff.
 *
 * When ON: navigating to /admin/properties (and nested routes) renders
 * the V2 admin shell. When OFF: those routes redirect back to the legacy
 * `/admin?tab=properties` OperatorConsole view — which is why an unset flag
 * makes the V2-only sections (Owners / Translations, with no legacy twin)
 * appear to "show Properties": the nav link resolves, the gate bounces it.
 *
 * Resolution order:
 *   1. Explicit `VITE_ADMIN_V2` env var wins everywhere ("true"/"1" → on,
 *      "false"/"0" → off) — "false"/"0" is the kill switch to fall back to
 *      legacy without a code change.
 *   2. Unset → ON everywhere, dev and production alike. V2 admin is the
 *      default UI now; no per-environment opt-in needed.
 */
export function isAdminV2Enabled(): boolean {
  // Vite inlines import.meta.env.VITE_* at build time.
  const raw = import.meta.env.VITE_ADMIN_V2;
  if (raw === "false" || raw === "0") return false;
  return true;
}

/**
 * Operator self-signup at /owners (COL-352, multi-operator SaaS).
 *
 * When ON: the /owners landing page offers account creation. When OFF:
 * the page renders a waitlist-style notice instead — the route exists but
 * cannot mint operator accounts.
 *
 * Same resolution order as VITE_ADMIN_V2: explicit env wins, unset → ON in
 * development, OFF in production. Keep this OFF on prod until the COL-358
 * isolation audit passes — no real external operator before that gate.
 */
export function isOperatorsSignupEnabled(): boolean {
  const raw = import.meta.env.VITE_OPERATORS_SIGNUP;
  if (raw === "true" || raw === "1") return true;
  if (raw === "false" || raw === "0") return false;
  return !!import.meta.env.DEV;
}

/**
 * colivIQ Brain (COL-527): the admin to-do deck at /admin/brain.
 * Unset → on in dev, off in prod — the same semantics as VITE_ADMIN_V2.
 */
export function isAdminBrainEnabled(): boolean {
  const raw = import.meta.env.VITE_ADMIN_BRAIN;
  if (raw === "true" || raw === "1") return true;
  if (raw === "false" || raw === "0") return false;
  return !!import.meta.env.DEV;
}

/**
 * The three owner-portal sections from the free-tier acquisition prototype:
 * Dokumente, Auszahlungen and Freunde werben.
 *
 * They ship as honest empty states, because the data behind them does not
 * exist yet: owner documents live nested under an apartment, owner-facing
 * payouts are still open (COL-461 / COL-510), and owner-to-owner referrals are
 * a separate programme. Adding them to the nav unflagged would give every
 * existing owner three menu items that go nowhere.
 *
 * Same resolution order as VITE_ADMIN_V2: explicit `VITE_OWNER_PORTAL_SECTIONS`
 * wins ("true"/"1" → on, "false"/"0" → off); unset → ON in development, OFF in
 * production.
 */
export function isOwnerPortalSectionsEnabled(): boolean {
  const raw = import.meta.env.VITE_OWNER_PORTAL_SECTIONS;
  if (raw === "true" || raw === "1") return true;
  if (raw === "false" || raw === "0") return false;
  return !!import.meta.env.DEV;
}

/**
 * A typed code instead of a confirmation link for owner signup.
 *
 * ⚠️ Turning this on WITHOUT the Supabase Auth change strands owners on a code
 * box with no code: the "Confirm signup" template must render `{{ .Token }}`,
 * which it does not by default. Flip the template and this flag together.
 *
 * ⚠️ The box has `OwnerVerify.CODE_LENGTH` inputs and both live and dev are
 * configured with `mailer_otp_length = 8`. If that project setting is ever
 * changed, change `CODE_LENGTH` in the same breath — a box shorter than the
 * emailed code cannot accept it, and the form stays disabled forever.
 *
 * The link is never disabled — both can live in the same email, and
 * /owners/confirmed keeps working either way.
 *
 * Explicit `VITE_OWNER_SIGNUP_OTP` wins; unset → OFF everywhere, including
 * development, because the code box is useless until the template sends one.
 */
export function isOwnerSignupOtpEnabled(): boolean {
  const raw = import.meta.env.VITE_OWNER_SIGNUP_OTP;
  return raw === "true" || raw === "1";
}

/**
 * Server-side gate for owner free-tier self-signup.
 *
 * There is no env var here on purpose: the authority is the database column
 * `platform_settings.owner_self_signup_enabled`, checked inside
 * `handle_new_user()`, because a direct Auth API call would walk straight past
 * any client flag. Read it through `useOwnerSelfSignupEnabled()`; this comment
 * is the checklist for turning it on.
 *
 * BEFORE SETTING IT TRUE ON LIVE:
 *
 *  1. Confirm the "Confirm signup" mail template and `mailer_otp_length` agree
 *     with `OwnerVerify.CODE_LENGTH` (see `isOwnerSignupOtpEnabled` above).
 *
 *  2. ⚠️ DECIDE THE `lessors.email` FALLBACK FIRST. Both
 *     `owner_visible_properties()` and `lessor_candidates_for_owner_user()`
 *     resolve an account to its portfolio by `property_owners.owner_user_id`
 *     OR by `lower(lessors.email) = lower(profiles.email)`. That email join
 *     predates this feature (20260815090000 and earlier) and several other
 *     read paths share it, so it is NOT something this flag's migration
 *     changed. What the flag changes is who can mint a profile at an arbitrary
 *     address: today an admin decides, with self-signup the only requirement
 *     is control of the mailbox. So a self-signup confirmed at an unclaimed
 *     `lessors.email` — a shared alias, a former employee's address — would
 *     see that lessor's portfolio with nobody having approved it.
 *
 *     It is not an exploit (the mailbox still has to be controlled), and it is
 *     harmless while this flag is false, which is why it does not block the
 *     merge. It is also not safe to "just fix" in one function: the same join
 *     is load-bearing in the notification feed, the apartment lessor
 *     normalisation and management-invoice owner resolution, and splitting
 *     them apart would show an owner a property in one surface and not in
 *     another. Either accept it deliberately, or exclude the email fallback
 *     for self-signed-up accounts across ALL of those paths in one change.
 *     Raised by Ganesh on #1699.
 */

/**
 * Owner portal V3 (COL-528): the top tab strip becomes a 200 px left sidebar,
 * and Settings moves from the nav into the user block's menu.
 *
 * Scoped to owner-portal accounts. Staff and operators keep the horizontal
 * strip whatever this is set to — `V2AdminNavbar` is mounted by ~25 pages that
 * are shared with them, and the redesign is an owner-facing one.
 *
 * ⚠️ Turning this on WITHOUT `VITE_OWNER_PORTAL_SECTIONS` gives a sidebar with
 * three fewer items (Dokumente / Auszahlungen / Freunde werben are
 * gated separately, see above). That is a legitimate state — an owner on the
 * old sections with the new shell — but it is not the design, so flip both
 * together when rolling out.
 *
 * Unlike the flags above this one defaults OFF everywhere, development
 * included: the shell lands before the pages it frames, and a half-built
 * sidebar is worse for local work than the strip that already works. Turn it
 * on explicitly with `VITE_OWNER_PORTAL_V3=true`.
 */
/**
 * `VITE_OWNER_PORTAL_V3=false`, explicitly.
 *
 * Distinct from "not enabled", because the rollout has a second way in: an
 * admin impersonating an owner gets the V3 shell with the flag merely unset
 * (useOwnerPortalV3). An explicit false has to beat that, or an admin could
 * never look at what an owner currently sees.
 */
export function isOwnerPortalV3ForcedOff(): boolean {
  const raw = import.meta.env.VITE_OWNER_PORTAL_V3;
  return raw === "false" || raw === "0";
}

export function isOwnerPortalV3Enabled(): boolean {
  const raw = import.meta.env.VITE_OWNER_PORTAL_V3;
  return raw === "true" || raw === "1";
}

// PORTED FROM DEVELOP for the tenant dashboard V2 port.
// Unset resolves OFF on main's production builds (DEV is false there), so
// the dashboard lands inert and is switched on deliberately.
/**
 * Tenant dashboard V2 (COL-533): the tenant's own dashboard collapses from
 * nine tabs to five that unlock by journey phase — Übersicht · Mein Zimmer ·
 * Community · Empfehlungen · Hilfe — with a separate set for applicants who
 * have no booking yet (Bewerbungen · Favoriten · Hilfe).
 *
 * Same resolution order as the rest of this file: explicit
 * `VITE_TENANT_DASHBOARD_V2` wins ("true"/"1" → on, "false"/"0" → off), unset
 * → ON in development, OFF in production. That is what puts it in front of
 * dev tenants without touching live, matching the owner-portal rollout.
 *
 * ⚠️ The five tabs are a re-grouping of the existing nine, not a superset:
 * Vertrag, Zahlungen and Ein-/Auszug all merge into "Mein Zimmer". Anything
 * that deep-links to the old tabs must go through `resolveLegacyTabParam`
 * (src/lib/tenant-dashboard-tabs.ts) rather than matching tab names directly —
 * five edge functions email `?tab=contract`, `?tab=checkin` and
 * `?tab=payments` links that are already sitting in tenants' inboxes.
 */
export function isTenantDashboardV2Enabled(): boolean {
  const raw = import.meta.env.VITE_TENANT_DASHBOARD_V2;
  if (raw === "true" || raw === "1") return true;
  if (raw === "false" || raw === "0") return false;
  return !!import.meta.env.DEV;
}
